import Dialog from '@comp/Dialog/Dialog';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  DOW2_DAILY_LIMIT_REACHED,
  DOW2_LAST_DOUBT_OF_DAY,
  DOW2_PENDING_DOUBTS
} from '@constants/segment';
import Button from '@base/Button/Button';
import Divider from '@base/Divider/Divider';
import { mediaQuery, Spacings } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsFreeUser } from '@api/selectors/auth';
import useCustomDoubtsStore from '@stores/customDoubtsStore';
import { getLimitFlag } from '@stores/selectors/customDoubtSelectors';
import {
  BOTTOMSHEET_GOT_IT,
  BOTTOMSHEET_VIEWED,
  GET_SUBSCRIPTION
} from '@cont/ClassVideoPlayer/constants';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';

const StyDialog = styled(Dialog)``;

const DialogWrapper = styled.div`
  padding: 40px 48px;
  width: 560px;
  text-align: center;
  box-sizing: border-box;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const Heading = styled.div`
  margin: 0 auto;
  font-weight: 700;
  font-size: 24px;
  line-height: 120%;
  color: var(--color-text-primary);
`;

const Desc = styled.div`
  margin: 8px auto 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: var(--color-text-secondary);
`;

const SubDesc = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 150%;
  color: var(--color-text-primary);
  margin-bottom: 24px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  padding: 0 8px;
  gap: 16px;

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const StyledButton = styled(Button)`
  width: 216px;
`;
const StyledDivider = styled(Divider)`
  width: ${Spacings.SPACING_20B};
  margin: 24px auto;
`;

const LimitDialog = ({
  goalUID,
  onCloseModal,
  heading,
  message,
  imgComp,
  msgWidth,
  statusCode,
  askAnEducator
}) => {
  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const limitFlag = useCustomDoubtsStore(getLimitFlag);
  const {
    doubtConfigData: { dailyRemainingDoubtsCount }
  } = useDoubtConfig(goalUID);

  const {
    query: { goalSlug }
  } = useRouter();
  const handleSubscription = () => {
    segment.track(GET_SUBSCRIPTION, {
      goal_name: routeGoalSegmentData.goal_name,
      goal_uid: routeGoalSegmentData.goal_uid,
      banner_type: 'Bottom Sheet',
      value_offering: 'AskADoubt',
      value_offering_cap: '1 Doubt asked'
    });
    onCloseModal();
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe`,
      {},
      'goal'
    );
  };

  const isFreeUser = useMyInfo(getIsFreeUser(goalUID));
  useEffect(() => {
    if (statusCode === 1) {
      segment.track(DOW2_DAILY_LIMIT_REACHED, {
        ...routeGoalSegmentData
      });
    } else if (statusCode === 2) {
      segment.track(DOW2_LAST_DOUBT_OF_DAY, {
        click_a_picture_clicked: askAnEducator ? 'No' : 'Yes',
        ...routeGoalSegmentData
      });
    } else if (statusCode === 3) {
      segment.track(DOW2_PENDING_DOUBTS, {
        doubt_type: 'Ask A Doubt',
        ask_an_educator_clicked: askAnEducator ? 'Yes' : 'No',
        click_a_picture_clicked: askAnEducator ? 'No' : 'Yes',
        ...routeGoalSegmentData
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dailyRemainingDoubtsCount) return;
    if (isFreeUser && dailyRemainingDoubtsCount > 0) onCloseModal();
    else if (isFreeUser && dailyRemainingDoubtsCount === 0) {
      segment.track(BOTTOMSHEET_VIEWED, {
        goal_name: routeGoalSegmentData.goal_name,
        goal_uid: routeGoalSegmentData.goal_uid,
        value_offering: 'AskADoubt',
        value_offering_cap: '1 Doubt asked'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreeUser, dailyRemainingDoubtsCount]);

  const handleGotClick = () => {
    onCloseModal();
    segment.track(BOTTOMSHEET_GOT_IT, {
      goal_name: routeGoalSegmentData.goal_name,
      goal_uid: routeGoalSegmentData.goal_uid,
      value_offering: 'AskADoubt',
      value_offering_cap: '1 Doubt asked'
    });
  };

  if (!isFreeUser)
    return (
      <StyDialog
        onClose={onCloseModal}
        dialogState={limitFlag}
        heading={heading}
        message={message}
        acceptLabel="Got it"
        acceptOnClick={onCloseModal}
        acceptLabelVariant="hollow"
        acceptLabelTheme="black"
        styledStringMsgWrapper=""
        aboveHeading={imgComp}
        messageWidth={msgWidth}
      />
    );

  if (dailyRemainingDoubtsCount === undefined || dailyRemainingDoubtsCount > 0)
    return null;
  return (
    <StyDialog onClose={onCloseModal} dialogState={limitFlag}>
      <DialogWrapper>
        <Heading>You’ve already asked your doubts today</Heading>
        <Desc>You can ask more doubts tomorrow</Desc>
        <StyledDivider />
        <SubDesc>Get doubt solved by educators with Iconic</SubDesc>
        <ButtonWrapper>
          <StyledButton
            label="Got it"
            variant="hollow"
            color="black"
            onClick={handleGotClick}
          />
          <StyledButton label="Get subscription" onClick={handleSubscription} />
        </ButtonWrapper>
      </DialogWrapper>
    </StyDialog>
  );
};

export default LimitDialog;
